import type { MessageDescriptor } from '@lingui/core';
import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { formatNumber } from '../i18n/format';
import { MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select } from 'antd';
import * as api from '../lib/api';
import { compareTaskRecency } from '../lib/taskOrdering';
import type { Channel } from '../types';
import { STATUS_COLUMNS, STATUS_CONFIG } from '../lib/taskStatus';
import { useTaskStore } from '../stores/useTaskStore';
import TaskRowActions from './TaskRowActions';
import ParticipantAvatars from './ParticipantAvatars';
import surface from './ProjectSurface.module.css';
import styles from './ServerTasksPage.module.css';
import ProjectPageHeader from './ProjectPageHeader';

export default function ServerTasksPage({
  guildId,
  activeThreadId,
  onTaskClick,
}: {
  guildId: string;
  activeThreadId?: string;
  onTaskClick: (task: api.Task) => void;
}) {
  const { i18n } = useLingui();
  const byTaskId = useTaskStore((state) => state.byTaskId);
  const requestedChannelId = typeof window === 'undefined'
    ? undefined
    : new URLSearchParams(window.location.search).get('channel') ?? undefined;
  const [channels, setChannels] = useState<Channel[]>([]);
  const [members, setMembers] = useState<api.ChannelMember[]>([]);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<MessageDescriptor | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string>();
  const [channel, setChannel] = useState<string | undefined>(requestedChannelId);
  const [assignee, setAssignee] = useState<string>();
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setReady(false);
    setLoadError(null);
    try {
      const [result, roster] = await Promise.all([
        api.fetchGuildChannels(guildId),
        api.fetchGuildMembers(guildId),
        useTaskStore.getState().loadGuildTasks(guildId),
      ]);
      setChannels(result.channels);
      setMembers(roster);
    } catch {
      setChannels([]);
      setMembers([]);
      useTaskStore.setState((state) => ({
        byTaskId: Object.fromEntries(
          Object.entries(state.byTaskId).filter(([, task]) => task.guild_id !== guildId),
        ),
      }));
      setLoadError(msg`Could not load Project tasks.`);
    } finally {
      setReady(true);
    }
  }, [guildId]);
  useEffect(() => {
    void load();
  }, [load]);
  const tasks = useMemo(
    () =>
      Object.values(byTaskId)
        .filter((task) => {
          if (task.guild_id !== guildId) return false;
          const haystack =
            `${task.title} ${task.description} ${task.channel?.channel_name ?? ''}`.toLowerCase();
          return (
            (!query || haystack.includes(query.toLowerCase())) &&
            (!status || task.status === status) &&
            (!channel || task.channel_id === channel) &&
            (!assignee || task.assignee_id === assignee)
          );
        })
        .sort(compareTaskRecency),
    [assignee, byTaskId, channel, guildId, query, status],
  );
  const update = (updated: api.Task) =>
    useTaskStore.setState((state) => ({ byTaskId: { ...state.byTaskId, [updated.id]: updated } }));
  const remove = (taskId: string) =>
    useTaskStore.setState((state) => {
      const next = { ...state.byTaskId };
      delete next[taskId];
      return { byTaskId: next };
    });
  if (!ready) return <div className={surface.loading}><Trans>Loading Project tasks…</Trans></div>;
  if (loadError)
    return (
      <main className={surface.pageRoot}>
        <section className={`${surface.pageContainer} ${surface.section}`}>
          <div className={surface.listEmpty} role="alert">
            {i18n._(loadError)}
            <button type="button" className="btn btn-sm" onClick={() => void load()}>
              <Trans>Retry</Trans></button>
          </div>
        </section>
      </main>
    );
  return (
    <main className={surface.pageRoot}>
      <ProjectPageHeader
        title={i18n._(msg`Tasks`)}
        end={<Button type="primary" icon={<PlusOutlined aria-hidden="true" />} onClick={() => setCreating(true)}><Trans>New Task</Trans></Button>}
      />
      <div className={`${surface.pageContainer} ${styles.toolbar}`}>
        <Input
          className={styles.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={i18n._(msg`Search tasks…`)}
          aria-label={i18n._(msg`Search Project tasks`)}
        />
        <Select
          allowClear
          value={status}
          onChange={setStatus}
          placeholder={i18n._(msg`Status`)}
          options={STATUS_COLUMNS.map(({ key, label }) => ({ value: key, label: i18n._(label) }))}
        />
        <Select
          allowClear
          value={channel}
          onChange={setChannel}
          placeholder={i18n._(msg`Channel`)}
          options={channels.map((item) => ({ value: item.id, label: `# ${item.name}` }))}
        />
        <Select
          allowClear
          value={assignee}
          onChange={setAssignee}
          placeholder={i18n._(msg`Assignee`)}
          options={members.map((item) => ({ value: item.user_id, label: item.display_name }))}
        />
      </div>
      <section className={`${surface.pageContainer} ${surface.section}`}>
        <h2 className={surface.sectionTitle}>
          {query ? i18n._(msg`Search results`) : i18n._(msg`All tasks`)}
          {tasks.length ? ` · ${formatNumber(i18n, tasks.length)}` : ''}
        </h2>
        <div className={surface.borderedList}>
          {tasks.length ? (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                active={task.thread_id === activeThreadId}
                onClick={onTaskClick}
                onUpdated={update}
                onDeleted={remove}
                channels={channels}
                members={members}
              />
            ))
          ) : (
            <div className={surface.listEmpty}><Trans>No matching tasks.</Trans></div>
          )}
        </div>
      </section>
      {creating && (
        <CreateServerTask
          channels={channels}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </main>
  );
}
function TaskRow({
  task,
  active,
  onClick,
  onUpdated,
  onDeleted,
  channels,
  members,
}: {
  task: api.Task;
  active: boolean;
  onClick: (task: api.Task) => void;
  onUpdated: (task: api.Task) => void;
  onDeleted: (id: string) => void;
  channels: Channel[];
  members: api.ChannelMember[];
}) {
  const { i18n } = useLingui();
  const cfg = STATUS_CONFIG[task.status];
  const channelName =
    task.channel?.channel_name ?? channels.find((item) => item.id === task.channel_id)?.name;
  const participants = (task.participants ?? [])
    .map((id) => members.find((member) => member.user_id === id))
    .filter((member): member is api.ChannelMember => !!member)
    .map((member) => ({
      user_id: member.user_id,
      display_name: member.display_name,
      avatar_url: member.avatar_url ?? undefined,
    }));
  return (
    <div className={`${surface.listRow} ${styles.taskRow} ${active ? surface.listRowActive : ''}`}>
      <button
        type="button"
        className={styles.rowOpener}
        onClick={() => task.thread_id && onClick(task)}
        disabled={!task.thread_id}
        aria-label={task.thread_id ? i18n._(msg`Open ${task.title}`) : i18n._(msg`${task.title} has no task thread`)}
      >
        <span className={styles.status} style={{ background: cfg?.color ?? '#a1a1aa' }} />
        <span className={styles.copy}>
          <strong>{task.title}</strong>
          <small>{channelName ? `#${channelName}` : i18n._(msg`Channel unavailable`)}</small>
        </span>
        <span className={styles.meta}>{cfg ? i18n._(cfg.label) : task.status}</span>
        {participants.length > 0 && <ParticipantAvatars participants={participants} size={18} />}
        {task.thread_id && <MessageOutlined aria-hidden="true" className={styles.thread} />}
      </button>
      <TaskRowActions task={task} onUpdated={onUpdated} onDeleted={onDeleted} />
    </div>
  );
}
function CreateServerTask({
  channels,
  onClose,
  onCreated,
}: {
  channels: Channel[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { i18n } = useLingui();
  const [channelId, setChannelId] = useState<string>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [roster, setRoster] = useState<{ channelId: string; members: api.ChannelMember[] } | null>(null);
  const [rosterError, setRosterError] = useState<MessageDescriptor | null>(null);
  const [saveError, setSaveError] = useState<MessageDescriptor | null>(null);
  const selectedChannel = channels.find((item) => item.id === channelId);
  const members = roster && roster.channelId === channelId ? roster.members : [];
  const rosterReady = !!channelId && roster?.channelId === channelId;
  const assigneeAvailable = !assigneeId ||
    (rosterReady && members.some((member) => member.user_id === assigneeId));
  const canSave = !!channelId && selectedChannel?.is_member === true &&
    !!title.trim() && !saving && assigneeAvailable;
  useEffect(() => {
    let current = true;
    setRoster(null);
    setRosterError(null);
    if (!channelId || selectedChannel?.is_member !== true) return;
    void api.fetchChannelMembers(channelId).then((members) => {
      if (current) setRoster({ channelId, members });
    }).catch(() => {
      if (current) setRosterError(msg`Could not load channel members. Close and reopen this form to retry.`);
    });
    return () => { current = false; };
  }, [channelId, selectedChannel?.is_member]);
  const save = async () => {
    if (!channelId || !canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.createTask(channelId, {
        title: title.trim(),
        description: description.trim(),
        assignee_id: assigneeId,
      });
      onCreated();
    } catch (error) {
      setSaveError(errorMessage(error, msg`Could not create the task. Your inputs are still here.`));
      setSaving(false);
    }
  };
  return (
    <Modal
      title={i18n._(msg`New Task`)}
      open
      onCancel={onClose}
      onOk={save}
      okText={saving ? i18n._(msg`Creating…`) : i18n._(msg`Create`)}
      okButtonProps={{ disabled: !canSave }}
    >
      <label className={styles.modalField}>
        <Trans>Channel</Trans> <Select
          value={channelId}
          onChange={(value) => {
            setChannelId(value);
            setAssigneeId(undefined);
            setRoster(null);
            setSaveError(null);
          }}
          placeholder={i18n._(msg`Select a channel`)}
          options={channels.filter((item) => item.is_member === true).map((item) => ({ value: item.id, label: `# ${item.name}` }))}
        />
      </label>
      <label className={styles.modalField}>
        <Trans>Title</Trans> <Input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className={styles.modalField}>
        <Trans>Description</Trans> <Input.TextArea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
        />
      </label>
      <label className={styles.modalField}>
        <Trans>Assignee</Trans> <Select
          allowClear
          disabled={!rosterReady}
          loading={!!channelId && !rosterReady && !rosterError}
          value={assigneeId}
          onChange={setAssigneeId}
          options={members.map((item) => ({ value: item.user_id, label: item.display_name }))}
        />
      </label>
      {(rosterError || saveError) && <div role="alert">{i18n._((saveError ?? rosterError)!)}</div>}
    </Modal>
  );
}
